import { useMemo } from 'react';
import { EmployeeTrendColumns } from 'types/reporting/trends';
import { t } from 'utils/getTranslations';

import { employeeHref, employeeLabel, ReportTone } from '../reportCells';
import { ChartPanel } from './ChartPanel';
import { fullMonthLabel, monthLabel, spansYears } from './chartFormat';
import { PivotSpec, sumByKey } from './pivotData';
import { PivotTable } from './PivotTable';
import { ReportBarChart } from './ReportBarChart';

type PeriodRow = EmployeeTrendColumns & { year: number; month?: string };

type Props<Row extends PeriodRow> = {
    rows: Row[];
    /** What a column and a bar stand for: a month (rows carry `month`) or a year. */
    period: 'month' | 'year';
    /** The figure the charts sum; stable across renders, as the pivot is rebuilt when it changes. */
    value: (row: Row) => number | null;
    /** What the figure is, such as "Billable hours". */
    measure: string;
    chartTitle: string;
    tableTitle: string;
    /** The total of every row, written beside the chart's title. */
    total: string;
    /** The pivot's tone: the figure's colour in the table reports. */
    tone: ReportTone;
};

/**
 * The two views of an employee trend report: the firm's figure per period as bars, and a pivot of employee
 * and job role against the periods with a Total column and a Total row. Both sum the same report rows.
 */
export function EmployeePeriodCharts<Row extends PeriodRow>({
    rows,
    period,
    value,
    measure,
    chartTitle,
    tableTitle,
    total,
    tone,
}: Props<Row>) {
    const periodKey = useMemo(
        () => (period === 'month' ? (row: Row) => row.month ?? '' : (row: Row) => `${row.year}`),
        [period]
    );
    const spec = useMemo<PivotSpec<Row>>(
        () => ({
            rowKey: (row) => [employeeLabel(row), row.jobRole],
            columnKey: periodKey,
            value,
            rowHref: (row) => employeeHref(row.employeeId),
        }),
        [periodKey, value]
    );
    const withYear = period === 'month' && spansYears(rows.map(periodKey));
    const columnLabel = (column: string) => (period === 'month' ? monthLabel(column, withYear) : column);
    const bars = sumByKey(rows, periodKey, value).map(({ key, value: sum }) => ({
        key,
        label: columnLabel(key),
        tooltipLabel: period === 'month' ? fullMonthLabel(key) : key,
        value: sum,
    }));
    return (
        <>
            <ChartPanel title={chartTitle} aside={total}>
                <ReportBarChart bars={bars} measure={measure} />
            </ChartPanel>
            <ChartPanel title={tableTitle} flush>
                <PivotTable
                    rows={rows}
                    spec={spec}
                    rowHeaders={[t('reporting.column.employee'), t('reporting.column.job-role')]}
                    columnLabel={columnLabel}
                    label={tableTitle}
                    showRowTotals
                    showColumnTotals
                    tone={tone}
                />
            </ChartPanel>
        </>
    );
}
